import { takeLatest, select, put, all, call } from 'redux-saga/effects';
import { get } from '@airasia-common/libraries/http';
import config from '@airasia-common/libraries/config';
import { UHF } from '@airasia-common/libraries/constants';
import { IGNORE_CACHE } from '../../constants';
import genericApiSaga from '../genericApiSaga';
import { actionCreators, types } from '../../redux/metadata/metadata.meta';
import { actionCreators as analyticsActionCreators } from '../../redux/analytics/analytics.meta';
import { LOB_HOTEL } from '../../redux/analytics/analytics.constants';
import { fetchSmartfill } from '../../redux/hotel/smartfill.meta.saga';

const DEFAULT_GEO_COUNTRY = 'my';

export function* fetchCountries({ payload }) {
  let language = payload?.languageInput || UHF.DEFAULT_LANGUAGE;

  const requestStart = new Date().getTime();
  function* onResponse(response) {
    if (response.status) {
      const responseEnd = new Date().getTime();
      yield put(
        analyticsActionCreators.trackReceiveApiResponse({
          api: 'Get countries',
          responseTime: responseEnd - requestStart,
          lob: LOB_HOTEL,
          pageId: payload?.pageId || 0,
        })
      );
    }
  }
  yield genericApiSaga({
    onResponse: onResponse,
    completed: actionCreators.fetchCountriesCompleted,
    gatewayCall: () =>
      get(`datasource/country/${language}/file.json`, {
        baseUrl: config.REACT_APP_STATIC_FLIGHT_BASE_URL,
      }),
  });
}

// TODO: rename HotelFeatureFlags to FeatureFlagsConfiguration
// These configurations are not tight to HOTEL
export function* fetchHotelFeatureFlags() {
  const env = config.REACT_APP_ENVIRONMENT.toLowerCase();
  yield genericApiSaga({
    completed: actionCreators.fetchHotelFeatureFlagsCompleted,
    failed: actionCreators.fetchHotelFeatureFlagsFailed,
    gatewayCall: () =>
      get(
        `hotels/featureflags/configuration/${env}/file.json?${IGNORE_CACHE}?${new Date()}`,
        {
          baseUrl: config.REACT_APP_NEW_METADATA_BASE_URL,
        }
      ),
  });
}

/** TODO: Remove this call altogether */
export function* fetchHotelConfig(state) {
  yield put(
    actionCreators.fetchHotelConfigCompleted({ currencies: state?.payload })
  );
}

export function* fetchHeaderCurrencies({ payload }) {
  const language = payload?.language || UHF.DEFAULT_LANGUAGE;
  yield genericApiSaga({
    completed: actionCreators.fetchHeaderCurrenciesCompleted,
    failed: actionCreators.fetchHeaderCurrenciesFailed,
    gatewayCall: () =>
      get(`common/currencies/${language}/file.json`, {
        baseUrl: config.REACT_APP_STATIC_COSMOS_BASE_URL,
      }),
  });
}

export function* fetchReverseGeoLocation({
  payload: { latitude: lat, longitude: lng },
}) {
  if (
    config.REACT_APP_GOOGLE_API_KEY &&
    config.REACT_APP_REVERSE_GEO_LOCATION_URL
  ) {
    yield genericApiSaga({
      completed: actionCreators.fetchReverseGeoLocationCompleted,
      failed: actionCreators.fetchHeaderCurrenciesFailed,
      gatewayCall: () =>
        get(
          `/geocode/json?latlng=${lat},${lng}&key=${config.REACT_APP_GOOGLE_API_KEY}`,
          {
            baseUrl: config.REACT_APP_REVERSE_GEO_LOCATION_URL,
          }
        ),
    });
  } else {
    yield put(actionCreators.fetchReverseGeoLocationFailed());
  }
}

export function* fetchUHFData({
  payload: { language = UHF.DEFAULT_LANGUAGE },
}) {
  const requestHeaderPath = 'universalheader';
  const requestFooterPath = 'universalfooter';

  yield genericApiSaga({
    completed: actionCreators.fetchUHFDataCompleted,
    failed: actionCreators.fetchUHFDataFailed,
    gatewayCall: async () => {
      const [headerRes, footerRes] = await Promise.all([
        get(`uhf/${requestHeaderPath}/${language}/file.json`, {
          baseUrl: config.REACT_APP_STATIC_COSMOS_BASE_URL,
        }),
        get(`uhf/${requestFooterPath}/${language}/file.json`, {
          baseUrl: config.REACT_APP_STATIC_COSMOS_BASE_URL,
        }),
      ]);
      const { status: headerStatus, body: headerData } = headerRes;
      const { status: footerStatus, body: footerData } = footerRes;
      return {
        status:
          headerStatus === 200 && footerStatus === 200
            ? 200
            : headerStatus || footerStatus,
        body: { headerData, footerData },
      };
    },
  });
}

export function* fetchUHSWData({
  payload: { language = UHF.DEFAULT_LANGUAGE },
}) {
  const staticUrl =
    config.REACT_APP_ENVIRONMENT === 'PRODUCTION'
      ? `https://static.airasia.com/`
      : `https://static.stgairasia.com/`;

  yield genericApiSaga({
    completed: actionCreators.fetchUHSWDataCompleted,
    failed: actionCreators.fetchUHSWDataFailed,
    gatewayCall: async () =>
      get(`cosmos/universalsearchwidget/hotel/${language}/file.json`, {
        baseUrl: staticUrl,
      }),
  });
}

export function* fetchPopupData({
  payload: { language = UHF.DEFAULT_LANGUAGE, country },
}) {
  const staticUrl =
    config.REACT_APP_ENVIRONMENT === 'PRODUCTION'
      ? `https://static.airasia.com/`
      : `https://static.stgairasia.com/`;

  yield genericApiSaga({
    completed: actionCreators.fetchPopupDataCompleted,
    failed: actionCreators.fetchPopupDataFailed,
    gatewayCall: async () =>
      get(`hotels/hotels_config/${country}-popup/${language}/file.json`, {
        baseUrl: staticUrl,
      }),
  });
}

export function* fetchHotelTranslations({ payload }) {
  const language = payload?.language || UHF.DEFAULT_LANGUAGE;
  yield genericApiSaga({
    completed: actionCreators.fetchHotelTranslationsCompleted,
    failed: actionCreators.fetchHotelTranslationsFailed,
    gatewayCall: () =>
      get(
        `/snap/lob/translations/${language}/file.json?ignoreCache=1?${new Date()}`,
        {
          baseUrl: config.REACT_APP_NEW_METADATA_BASE_URL,
        }
      ),
  });
}

export function* fetchInitialCall({ payload }) {
  if (payload?.languageId) {
    yield all([
      payload?.locationId &&
        call(fetchSmartfill, {
          locationId: payload?.locationId,
          languageId: payload?.languageId,
        }),
      call(fetchHeaderCurrencies, { language: payload?.languageId }),
      call(fetchCountries, { languageInput: payload?.languageId, pageId: 0 }),
      call(fetchHotelFeatureFlags),
      payload?.supportedCurrencies &&
        call(fetchHotelConfig, { payload: payload?.supportedCurrencies }),
    ]);
  }
}

export default function* createCountriesSaga() {
  yield takeLatest(types.FETCH_COUNTRIES, fetchCountries);
  yield takeLatest(types.FETCH_HOTEL_FEATURE_FLAGS, fetchHotelFeatureFlags);
  yield takeLatest(types.FETCH_HEADER_CURRENCIES, fetchHeaderCurrencies);
  yield takeLatest(types.FETCH_HOTEL_CONFIG, fetchHotelConfig);
  yield takeLatest(types.FETCH_REVERSE_GEO_LOCATION, fetchReverseGeoLocation);
  yield takeLatest(types.FETCH_UHF_DATA, fetchUHFData);
  yield takeLatest(types.FETCH_UHSW_DATA, fetchUHSWData);
  yield takeLatest(types.FETCH_POPUP_DATA, fetchPopupData);
  yield takeLatest(types.FETCH_INITIAL_CALL, fetchInitialCall);
  yield takeLatest(types.FETCH_HOTEL_TRANSLATIONS, fetchHotelTranslations);
}
